import type { ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";

import { LocaleProvider, useLocale } from "@/context/locale-provider";
import { SessionProvider } from "@/context/session-provider";
import { ApiError } from "@/config";
import { localeDir } from "@/utils/locale";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      /* Transient blips (backend restart, ALB 502, dropped connection) are the
         common reason an otherwise-healthy screen flashes the load-failed
         banner. Four attempts with backoff outlast a typical Nest watch bounce
         without making a real 500 feel stuck. */
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
          return false;
        }
        return failureCount < 4;
      },
      retryDelay: (attemptIndex) => Math.min(1_000 * 2 ** attemptIndex, 8_000),
      refetchOnWindowFocus: true,
    },
  },
});

function ToasterHost() {
  const { locale } = useLocale();
  return (
    <Toaster
      position={locale === "ar" ? "bottom-left" : "bottom-right"}
      dir={localeDir(locale)}
      richColors
      closeButton
    />
  );
}

export default function Providers({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider>
        <LocaleProvider>
          {children}
          <ToasterHost />
        </LocaleProvider>
      </SessionProvider>
    </QueryClientProvider>
  );
}
